import { injectable } from 'inversify';
import Papa from 'papaparse';
import * as yup from 'yup';
import { CsvDocumentInput, Formats } from '../generated/types';
import { TranslationService } from '../services/translationService';
import { Emptyable } from '../util/emptyable';
import { fileInfos } from '../util/file';
import { DATE_TIME_IMPORT_DEFAULT_FORMAT, FORM_FORMAT, TIME_FORMAT } from '../util/luxon';
import { MimeType, mimeType } from '../util/mimeType';
import { InputService } from './inputService';

export const MAX_CSV_SIZE = 20_000_000; // 20 MB. Arbitrary.

export interface CsvSettings {
    delimiter?: string;
    quoteChar?: string;
    newline?: '\r' | '\n' | '\r\n';
}

@injectable()
export class CsvInputService extends InputService {
    constructor(translationService: TranslationService) {
        super(translationService);
    }

    csvDocumentInputDefault(formats: Emptyable<Formats>): CsvDocumentInput {
        return {
            key: '',
            name: '',
            acl: '',
            formats: {
                timeFormat: TIME_FORMAT,
                dateFormat: FORM_FORMAT,
                datetimeFormat: DATE_TIME_IMPORT_DEFAULT_FORMAT,
                ...formats
            }
        };
    }

    schema() {
        const required = this.t('le_document_est_86403');

        return yup.object().shape({
            acl: yup.string().required(required),
            delimiter: yup
                .string()
                .optional()
                .test('is-valid-value', this.t('delimiter_value_52108'), (delim?: string) =>
                    delim ? !Papa.BAD_DELIMITERS.includes(delim) : true
                ),
            key: yup
                .string()
                .required(required)
                .test(
                    'is-a-csv',
                    this.t('le_fichier_n_es_33134'),
                    (key: string) => mimeType(fileInfos(key).extension) === MimeType.Csv
                ),
            name: yup.string().required(required),
            newline: yup
                .string()
                .optional()
                .test('is-valid-value', this.t('newline_charact_88066'), (newline?: string) =>
                    newline ? ['\r', '\n', '\r\n'].includes(newline) : true
                ),
            quoteChar: yup.string().optional(),
            formats: yup.object().shape({
                timeFormat: yup.string().optional(),
                dateFormat: yup.string().optional(),
                datetimeFormat: yup.string().optional()
            }),
            file: yup
                .mixed()
                .test('is-not-empty', this.t('the_file_is_emp_77243'), (file) =>
                    file ? (file as File).size > 0 : true
                )
                .test('is-not-too-big', this.t('le_fichier_est_17522'), (file) =>
                    file ? (file as File).size < MAX_CSV_SIZE : true
                )
        });
    }
}
